import clsx from 'clsx';
import * as React from 'react';
import { ReactChild, ReactFragment, ReactPortal, useState } from 'react';

import { Spinner } from '@/components/Elements/Spinner';

export const variants = {
  primary: 'md__payments__btn md__payments__btn--primary',
  secondary: 'md__payments__btn md__payments__btn--secondary',
  ghost: 'md__payments__btn md__payments__btn--ghost',
  'primary--alt': 'md__payments__btn md__payments__btn--primary--alt',
  danger: 'md__payments__btn md__payments__btn--danger',
  rounded: 'md__payments__btn md__payments__btn--rounded',
  'link--black': 'md__payments__btn btn--link--black no-box-shadow',
  raw: 'md__payments__btn--raw',
};

const sizes = {
  sm: 'md__payments__btn--sm',
  md: 'md__payments__btn--md',
  lg: 'md__payments__btn--lg',
  full: 'md__payments__btn--full',
  free: 'md__payments__btn--free',
};

type IconProps =
  | { startIcon: React.ReactElement; endIcon?: never }
  | { endIcon: React.ReactElement; startIcon?: never }
  | { endIcon?: undefined; startIcon?: undefined };

type SpinnerProps = 'start' | 'end';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  isLoading?: boolean;
  loadingText?: string;
  spinnerPlacement?: SpinnerProps;
  onClick?: any;
} & IconProps;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'button',
      className = '',
      variant = 'primary',
      size = 'md',
      isLoading: _isLoading = false,
      startIcon,
      endIcon,
      spinnerPlacement = 'end',
      loadingText: _loadingText,
      onClick,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState<true | ReactChild | ReactFragment | ReactPortal>(
      false
    );
    const onDefaultClick = () => {
      if (!_isLoading) {
        onClick && onClick();
      } else {
        setIsLoading(true);
        _loadingText && setLoadingText(_loadingText);
        onClick &&
          onClick(() => {
            setIsLoading(false);
            _loadingText && props.children && setLoadingText(props.children);
          });
      }
    };
    return (
      <button
        ref={ref}
        disabled={_isLoading || isLoading || disabled}
        type={type}
        className={clsx(variants[variant], sizes[size], className)}
        onClick={onDefaultClick}
        {...props}
      >
        {(!isLoading || !_isLoading) && startIcon}
        {(isLoading || _isLoading) && spinnerPlacement === 'start' && (
          <Spinner size="sm" className="text-current" />
        )}
        {props.children && (
          <span className="mx-2">
            {_isLoading ? _loadingText || props.children : loadingText || props.children}
          </span>
        )}
        {(!isLoading || !_isLoading) && endIcon}
        {(isLoading || _isLoading) && spinnerPlacement === 'end' && (
          <Spinner size="sm" className="text-current" />
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
